/**
 * Query Builder - Translates UI state into SQL strings for DuckDB-WASM execution
 * Uses display names (users/events) in SQL preview while DuckDB uses viz1_* internally
 */

// ─── Display Name Mapping ───────────────────────────────
// No longer needed as internal names match display names now
export function getDisplayName(internalName) {
    return internalName;
}

export function getInternalName(displayName) {
    return displayName;
}

// ─── Constants ──────────────────────────────────────────
export const AGGREGATION_FUNCTIONS = [
    { value: 'COUNT', label: 'Count' },
    { value: 'COUNT_DISTINCT', label: 'Count Distinct' },
    { value: 'SUM', label: 'Sum' },
    { value: 'AVG', label: 'Average' },
    { value: 'MIN', label: 'Minimum' },
    { value: 'MAX', label: 'Maximum' },
    { value: 'CUSTOM', label: 'Custom Expression' },
];

export const JOIN_TYPES = [
    { value: 'JOIN', label: 'Inner Join' },
    { value: 'LEFT JOIN', label: 'Left Join' },
    { value: 'FULL JOIN', label: 'Full Join' },
];

export const DATE_GRANULARITIES = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: 'year', label: 'Year' },
];

export const FILTER_OPERATORS = {
    text: [
        { value: '=', label: 'equals' },
        { value: '!=', label: 'not equals' },
        { value: 'LIKE', label: 'contains' },
        { value: 'IN', label: 'is one of' },
        { value: 'IS NOT NULL', label: 'is not null' },
    ],
    number: [
        { value: '=', label: 'equals' },
        { value: '!=', label: 'not equals' },
        { value: '>', label: 'greater than' },
        { value: '<', label: 'less than' },
        { value: '>=', label: 'at least' },
        { value: '<=', label: 'at most' },
        { value: 'BETWEEN', label: 'between' },
        { value: 'IS NOT NULL', label: 'is not null' },
    ],
    date: [
        { value: '=', label: 'equals' },
        { value: '>', label: 'after' },
        { value: '<', label: 'before' },
        { value: '>=', label: 'on or after' },
        { value: '<=', label: 'on or before' },
        { value: 'BETWEEN', label: 'between' },
        { value: 'RELATIVE', label: 'relative' },
        { value: 'IS NOT NULL', label: 'is not null' },
    ],
};

export const RELATIVE_DATE_OPTIONS = [
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
];

// ─── Helpers ────────────────────────────────────────────
export function getColumnCategory(duckdbType) {
    const t = (duckdbType || '').toUpperCase();
    if (t.includes('INT') || t.includes('FLOAT') || t.includes('DOUBLE') || t.includes('DECIMAL') || t.includes('NUMERIC') || t.includes('BIGINT') || t.includes('SMALLINT') || t.includes('TINYINT')) {
        return 'number';
    }
    if (t.includes('DATE') || t.includes('TIMESTAMP')) {
        return 'date';
    }
    return 'text';
}

function escapeValue(value, category) {
    if (category === 'number') return value;
    return `'${String(value).replace(/'/g, "''")}'`;
}

/**
 * Build SQL from query state
 * Returns { displaySQL, execSQL } - displaySQL uses clean names, execSQL uses viz1_* names
 *
 * @param {Object} state
 * @param {string} state.table - internal table name (viz1_users / viz1_events)
 * @param {Object|null} state.join - { table, type, leftCol, rightCol }
 * @param {Array} state.filters - [{ column, operator, value }]
 * @param {Object} state.aggregation - { fn, column }
 * @param {Array} state.groupBy - [{ column, granularity? }]
 * @param {Object|null} state.orderBy - { column, direction }
 * @param {Object} schemas - { tableName: [{ name, type }] }
 */
export function buildSQL(state, schemas) {
    const { table, join, filters, aggregation, metrics, groupBy, orderBy, limit } = state;

    // Helper to qualify columns
    const qualify = (col, mode = 'exec') => {
        if (!col || col === '*') return col;

        // If schemas not ready, return raw
        if (!schemas) return col;

        const tableCols = schemas[table] || [];
        const joinCols = (join && join.table && schemas[join.table]) ? schemas[join.table] : [];

        const inTable = tableCols.some(c => c.name === col);
        const inJoin = joinCols.some(c => c.name === col);

        const tName = mode === 'exec' ? table : getDisplayName(table);
        const jName = join && join.table ? (mode === 'exec' ? join.table : getDisplayName(join.table)) : '';

        // Prioritize primary table if common column (matching getAvailableColumns logic)
        if (inTable) return `"${tName}".${col}`;
        if (inJoin) return `"${jName}".${col}`;
        return col;
    };

    // Build column type map for escaping
    const colTypes = {};
    if (schemas) {
        Object.values(schemas).forEach(cols => {
            if (Array.isArray(cols)) {
                cols.forEach(c => { colTypes[c.name] = c.type; });
            }
        });
    }

    // ─── FROM clause ──────────────────────────────
    const primaryDisplay = getDisplayName(table);
    let fromDisplay, fromExec;

    if (join && join.table) {
        const joinDisplay = getDisplayName(join.table);
        const rawJoinType = join.type || 'JOIN';
        const joinType = /^(INNER|LEFT|RIGHT|FULL|CROSS)$/i.test(rawJoinType.trim()) ? rawJoinType + ' JOIN' : rawJoinType;
        const leftCol = join.leftCol || join.on || 'user_id';
        const rightCol = join.rightCol || join.on || 'user_id';

        // Qualification for ON clause is explicit
        // But we should verify if these columns need quotes or if we assume simple names?
        // Let's use clean names but if we need to qualify them, we should.
        // The join logic assumes these columns exist on respective tables. 
        // We'll trust the join state or default to user_id.
        fromDisplay = `"${primaryDisplay}"\n${joinType} "${joinDisplay}" ON "${primaryDisplay}".${leftCol} = "${joinDisplay}".${rightCol}`;
        fromExec = `"${table}"\n${joinType} "${join.table}" ON "${table}".${leftCol} = "${join.table}".${rightCol}`;
    } else {
        fromDisplay = `"${primaryDisplay}"`;
        fromExec = `"${table}"`;
    }

    // ─── GROUP BY expressions ─────────────────────
    const groupByExprs = []; // display
    const groupByExprsExec = []; // exec
    const groupByAliases = [];

    if (groupBy && groupBy.length > 0) {
        groupBy.forEach(g => {
            const col = g.column;
            const qColDisplay = qualify(col, 'display');
            const qColExec = qualify(col, 'exec');

            if (g.bucket && g.bucket.size) {
                // Numeric bucketing: FLOOR(col / size) * size → human-readable range label
                const size = Number(g.bucket.size);
                const alias = `${col}_bucket`;
                const exprDisplay = `FLOOR(${qColDisplay} / ${size}) * ${size}`;
                const exprExec = `FLOOR(${qColExec} / ${size}) * ${size}`;
                groupByExprs.push(`${exprDisplay} AS ${alias}`);
                groupByExprsExec.push(`${exprExec} AS ${alias}`);
                groupByAliases.push(alias);
            } else if (g.granularity && g.granularity !== 'day') {
                const alias = `${col}_${g.granularity}`;
                groupByExprs.push(`DATE_TRUNC('${g.granularity}', ${qColDisplay}) AS ${alias}`);
                groupByExprsExec.push(`DATE_TRUNC('${g.granularity}', ${qColExec}) AS ${alias}`);
                groupByAliases.push(alias);
            } else {
                // For simple columns, always alias by bare column name so the result key
                // is a plain string (e.g. "city") even when the expression references a
                // joined table (e.g. "drivers".city). A dot in the key would be treated by
                // Recharts as a nested path accessor, breaking XAxis category lookup.
                const resultAlias = col;
                const aliasClause = join ? ` AS "${resultAlias}"` : '';

                groupByExprs.push(`${qColDisplay}${aliasClause}`);
                groupByExprsExec.push(`${qColExec}${aliasClause}`);
                groupByAliases.push(resultAlias);
            }
        });
    }

    // ─── SELECT clause ────────────────────────────
    // Support both old `aggregation` (single) and new `metrics` (array)
    // Filter out incomplete metrics where fn is still null (user hasn't finished configuring)
    const metricList = (metrics && metrics.length > 0)
        ? metrics.filter(m => m.fn)
        : (aggregation && aggregation.fn ? [aggregation] : []);

    // Helper: build one metric expression for a given mode ('display' | 'exec')
    const buildMetricExpr = (m, mode) => {
        if (m.fn === 'CUSTOM') {
            const alias = (m.alias || 'custom_expr').replace(/\s+/g, '_');
            return { expr: m.expression || 'NULL', alias };
        }
        const aggCol = m.column || '*';
        const qAggCol = aggCol === '*' ? '*' : qualify(aggCol, mode);
        const isAvgPerUser = m.fn === 'AVG_PER_USER';
        const isDistinct = m.fn === 'COUNT_DISTINCT';
        const fn = isDistinct ? 'COUNT' : m.fn;
        const innerState = isDistinct ? `DISTINCT ${qAggCol}` : qAggCol;
        const rawExpr = isAvgPerUser
            ? `ROUND(COUNT(*) * 1.0 / NULLIF(COUNT(DISTINCT ${qAggCol}), 0), 2)`
            : `${fn}(${innerState})`;
        // Cast SUM to DOUBLE in exec mode to prevent DuckDB returning HUGEINT
        // (Arrow JS serialises HUGEINT as an opaque object, not a plain JS number)
        const expr = (mode === 'exec' && fn === 'SUM')
            ? `CAST(${rawExpr} AS DOUBLE)`
            : rawExpr;
        const alias = isAvgPerUser
            ? 'avg_per_user'
            : isDistinct
                ? 'count_distinct'
                : `${m.fn.toLowerCase()}_${aggCol.replace(/[.*]/g, '') || 'all'}`;
        return { expr, alias };
    };

    let selectDisplay, selectExec;
    if (metricList.length > 0) {
        const metricExprsDisplay = metricList.map(m => { const { expr, alias } = buildMetricExpr(m, 'display'); return `${expr} AS ${alias}`; });
        const metricExprsExec = metricList.map(m => { const { expr, alias } = buildMetricExpr(m, 'exec'); return `${expr} AS ${alias}`; });
        if (groupByExprs.length > 0) {
            selectDisplay = `${groupByExprs.join(', ')}, ${metricExprsDisplay.join(', ')}`;
            selectExec = `${groupByExprsExec.join(', ')}, ${metricExprsExec.join(', ')}`;
        } else {
            selectDisplay = metricExprsDisplay.join(', ');
            selectExec = metricExprsExec.join(', ');
        }
    } else {
        // SELECT * expansion for Joins
        if (join && schemas) {
            const buildAllCols = (mode) => {
                const parts = [];
                // Primary Table
                if (schemas[table]) {
                    const tName = mode === 'exec' ? table : getDisplayName(table);
                    const tDisp = getDisplayName(table);
                    schemas[table].forEach(c => {
                        parts.push(`"${tName}"."${c.name}" AS "${tDisp}.${c.name}"`);
                    });
                }
                // Joined Table
                if (schemas[join.table]) {
                    const tName = mode === 'exec' ? join.table : getDisplayName(join.table);
                    const tDisp = getDisplayName(join.table);
                    schemas[join.table].forEach(c => {
                        // We include all columns even if duplicate name (e.g. user_id)
                        // The alias makes them distinct: users.user_id vs events.user_id
                        parts.push(`"${tName}"."${c.name}" AS "${tDisp}.${c.name}"`);
                    });
                }
                return parts.join(', ');
            };

            const colsDisplay = buildAllCols('display');
            const colsExec = buildAllCols('exec');

            selectDisplay = colsDisplay || '*';
            selectExec = colsExec || '*';
        } else {
            selectDisplay = '*';
            selectExec = '*';
        }
    }

    // ─── WHERE clause ─────────────────────────────
    let whereClause = '';
    let whereClauseExec = ''; // Need separate exec clause for qualification

    if (filters && filters.length > 0) {
        const processFilters = (mode) => filters
            .filter(f => f.column && f.operator && (f.operator === 'IS NOT NULL' || (f.value !== '' && f.value !== undefined)))
            .map(f => {
                const qCol = qualify(f.column, mode);
                const category = getColumnCategory(colTypes[f.column] || '');

                if (f.operator === 'IS NOT NULL') {
                    return `${qCol} IS NOT NULL`;
                }
                if (f.operator === 'IN') {
                    const vals = String(f.value).split(',').map(v => `'${v.trim().replace(/'/g, "''")}'`).join(', ');
                    return `${qCol} IN (${vals})`;
                }
                if (f.operator === 'LIKE') {
                    return `${qCol} LIKE '%${String(f.value).replace(/'/g, "''")}%'`;
                }
                if (f.operator === 'BETWEEN') {
                    const [start, end] = String(f.value).split('|');
                    if (start && end) {
                        if (category === 'number') {
                            return `${qCol} BETWEEN ${start} AND ${end}`;
                        }
                        return `${qCol} BETWEEN '${start}' AND '${end}'`;
                    }
                    return null;
                }
                if (f.operator === 'RELATIVE') {
                    const val = String(f.value).trim().toLowerCase();

                    // 1. Parse "last_X_units" (e.g., last_2_months, last_7_days)
                    const lastMatch = val.match(/^last_(\d+)_([a-z]+)$/i);
                    if (lastMatch) {
                        const amount = parseInt(lastMatch[1], 10);
                        let unit = lastMatch[2].toUpperCase();
                        if (unit.endsWith('S')) unit = unit.slice(0, -1); // remove trailing S
                        return `${qCol} >= CURRENT_DATE - INTERVAL ${amount} ${unit}`;
                    }

                    // 2. Parse "this_unit" (e.g., this_month, this_year)
                    const thisMatch = val.match(/^this_([a-z]+)$/i);
                    if (thisMatch) {
                        let unit = thisMatch[1].toLowerCase();
                        return `DATE_TRUNC('${unit}', ${qCol}) = DATE_TRUNC('${unit}', CURRENT_DATE)`;
                    }

                    // 3. Fallback to existing static mappings
                    switch (val) {
                        case 'last_month': return `DATE_TRUNC('month', ${qCol}) = DATE_TRUNC('month', CURRENT_DATE - INTERVAL 1 MONTH)`;
                        case 'last_year': return `DATE_TRUNC('year', ${qCol}) = DATE_TRUNC('year', CURRENT_DATE - INTERVAL 1 YEAR)`;
                        default: return null;
                    }
                }
                return `${qCol} ${f.operator} ${escapeValue(f.value, category)}`;
            })
            .filter(Boolean);

        const conditionsDisplay = processFilters('display');
        const conditionsExec = processFilters('exec');

        if (conditionsDisplay.length > 0) {
            whereClause = `WHERE ${conditionsDisplay.join('\n  AND ')}`;
            whereClauseExec = `WHERE ${conditionsExec.join('\n  AND ')}`;
        }
    }

    // ─── GROUP BY clause ──────────────────────────
    let groupByClause = '';
    let groupByClauseExec = '';

    if (groupByAliases.length > 0 && metricList.length > 0) {
        // Use the qualified columns for GROUP BY
        const getGroupCols = (mode) => groupBy.map(g => {
            const qCol = qualify(g.column, mode);
            if (g.bucket && g.bucket.size) {
                return `FLOOR(${qCol} / ${Number(g.bucket.size)}) * ${Number(g.bucket.size)}`;
            }
            if (g.granularity && g.granularity !== 'day') {
                return `DATE_TRUNC('${g.granularity}', ${qCol})`;
            }
            return qCol;
        });

        const rawGroupColsDisplay = getGroupCols('display');
        const rawGroupColsExec = getGroupCols('exec');

        groupByClause = `GROUP BY ${rawGroupColsDisplay.join(', ')}`;
        groupByClauseExec = `GROUP BY ${rawGroupColsExec.join(', ')}`;
    }

    // ─── ORDER BY clause ─────────────────────────
    let orderByClause = '';
    let orderByClauseExec = '';

    if (orderBy && orderBy.column) {
        // If ordering by an alias (like count_distinct), don't qualify.
        // If ordering by a raw column, qualify.
        // Current logic assumes aggregation alias or simple column.
        // It's safer to use the alias if it exists in SELECT.
        // But the previous implementation used `orderBy.column`. is that a raw column or alias?
        // Usually alias from the table click headers.
        // If it's a raw column (e.g. user_id), we should qualify it.

        // Check if it matches a select alias
        // Actually, just try to qualify it. If it's not in schemas, qualify returns it as is (good for aliases).
        const qColDisplay = qualify(orderBy.column, 'display');
        const qColExec = qualify(orderBy.column, 'exec');

        orderByClause = `ORDER BY ${qColDisplay} ${orderBy.direction || 'ASC'}`;
        orderByClauseExec = `ORDER BY ${qColExec} ${orderBy.direction || 'ASC'}`;

    } else if (groupByAliases.length > 0 && metricList.length > 0) {
        // Auto-order logic
        const firstGroup = groupBy[0];
        const firstCategory = getColumnCategory(colTypes[firstGroup?.column] || '');
        if (firstCategory === 'date') {
            orderByClause = `ORDER BY ${groupByAliases[0]} ASC`;
            orderByClauseExec = `ORDER BY ${groupByAliases[0]} ASC`;
        } else {
            const fm = metricList[0];
            const firstMetricAlias = fm.fn === 'CUSTOM'
                ? (fm.alias || 'custom_expr').replace(/\s+/g, '_')
                : fm.fn === 'COUNT_DISTINCT'
                    ? 'count_distinct'
                    : fm.fn === 'AVG_PER_USER'
                        ? 'avg_per_user'
                        : `${fm.fn.toLowerCase()}_${(fm.column || '').replace(/[.*]/g, '') || 'all'}`;
            orderByClause = `ORDER BY ${firstMetricAlias} DESC`;
            orderByClauseExec = `ORDER BY ${firstMetricAlias} DESC`;
        }
    }

    // ─── Assemble ─────────────────────────────────
    const limitClause = limit ? `LIMIT ${limit}` : '';
    const buildQuery = (sel, from, where, group, order, lim) => {
        return [
            `SELECT ${sel}`,
            `FROM ${from}`,
            where,
            group,
            order,
            lim,
        ].filter(Boolean).join('\n') + ';';
    };

    return {
        displaySQL: buildQuery(selectDisplay, fromDisplay, whereClause, groupByClause, orderByClause, limitClause),
        execSQL: buildQuery(selectExec, fromExec, whereClauseExec, groupByClauseExec, orderByClauseExec, limitClause),
    };
}

// ─── Column helpers ─────────────────────────────────────

/**
 * Get columns available given a table + optional join
 */
export function getAvailableColumns(table, join, schemas) {
    const cols = [];
    if (schemas[table]) {
        schemas[table].forEach(col => {
            cols.push({ name: col.name, type: col.type, source: getDisplayName(table) });
        });
    }
    if (join && join.table && schemas[join.table]) {
        schemas[join.table].forEach(col => {
            // Skip duplicate join column
            if (cols.some(c => c.name === col.name)) return;
            cols.push({ name: col.name, type: col.type, source: getDisplayName(join.table) });
        });
    }
    return cols;
}
